import { Link } from 'react-router-dom';
import { useSolutions, useSubscriptions, useTenantOnboarding } from '../../api/plans';
import { errorMessage } from '../../api/client';
import type { Solution, Subscription as SubscriptionRecord } from '../../api/types';
import { useMe } from '../../auth/AuthContext';
import { OnboardingChecklist } from '../../components/plans/OnboardingChecklist';
import { subscriptionLabel, subscriptionTone } from '../../components/plans/PlanCards';
import {
  Alert,
  Badge,
  Card,
  EmptyState,
  PageHeader,
  PageLoader,
  formatDate,
} from '../../components/ui';

/**
 * The plan the organization is on, everything it includes and how far
 * onboarding has come, then the solutions (WhatsApp, email) switched on for
 * it. No prices: those live on the marketing site, and plans change through
 * the Balsam contact.
 */
export function Subscription() {
  const me = useMe();
  const tenantId = me.principal.tenantId;
  const solutionsQuery = useSolutions();
  const subscriptionsQuery = useSubscriptions(tenantId);
  const onboardingQuery = useTenantOnboarding(tenantId);
  const error = solutionsQuery.isError
    ? errorMessage(solutionsQuery.error)
    : subscriptionsQuery.isError
      ? errorMessage(subscriptionsQuery.error)
      : onboardingQuery.isError
        ? errorMessage(onboardingQuery.error)
        : null;
  const solutions = solutionsQuery.data ?? null;
  const subscriptions = subscriptionsQuery.data ?? null;
  const onboarding = onboardingQuery.data ?? null;

  const live = (slug: string) =>
    subscriptions?.find((s) => s.solution === slug && s.status !== 'CANCELED') ?? null;
  const platform = live('platform');
  const platformPlan =
    solutions
      ?.find((s) => s.slug === 'platform')
      ?.plans.find((p) => p.slug === platform?.plan.slug) ?? null;
  const trialing = platform?.status === 'TRIALING' && platform.trialEnd !== null;

  return (
    <>
      <PageHeader
        eyebrow="Portal"
        title="Subscription"
        description="The plan your organization is on, what it includes, and how far onboarding has come. To change plans, talk to your Balsam contact."
      />
      {error ? <Alert kind="error">{error}</Alert> : null}
      {tenantId === null ? (
        <EmptyState title="No organization yet">
          Your account is ready, but no tenant has been created for it yet.
        </EmptyState>
      ) : null}

      <Card title="Your plan" subtitle="The Balsam plan your organization is on">
        {tenantId !== null && subscriptions === null && !error ? (
          <PageLoader label="Loading your plan…" />
        ) : null}
        {subscriptions && !platform ? (
          <EmptyState title="No plan yet">
            Your Balsam contact assigns the plan after the design session.
          </EmptyState>
        ) : null}
        {platform ? (
          <div className="stack">
            <div>
              <p className="subscription__plan">
                {platform.plan.name}{' '}
                <Badge tone={subscriptionTone(platform.status)}>
                  {subscriptionLabel(platform.status)}
                </Badge>
              </p>
              {platformPlan?.tagline ? <p className="muted">{platformPlan.tagline}</p> : null}
              {platformPlan?.bestFor ? (
                <p className="muted">Best for {platformPlan.bestFor}</p>
              ) : null}
            </div>
            <dl className="kv">
              <dt>Started</dt>
              <dd>{formatDate(platform.startedAt)}</dd>
              <dt>{trialing ? 'Trial ends' : 'Renews'}</dt>
              <dd>{formatDate(trialing ? platform.trialEnd : platform.currentPeriodEnd)}</dd>
            </dl>
            {platformPlan && platformPlan.features.length ? (
              <div>
                <p className="checklist__heading">Includes</p>
                <ul className="includes">
                  {platformPlan.features.map((f) => (
                    <li key={f}>{f}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </Card>

      <Card
        title="Onboarding"
        subtitle={
          onboarding?.subscription
            ? `${onboarding.progress.activated} of ${onboarding.progress.total} activated`
            : 'What your plan includes, and what is switched on so far'
        }
      >
        {tenantId !== null && onboarding === null && !error ? (
          <PageLoader label="Loading the checklist…" />
        ) : null}
        {onboarding && !onboarding.subscription ? (
          <EmptyState title="Nothing to activate yet">
            The checklist appears once your plan is assigned.
          </EmptyState>
        ) : null}
        {onboarding?.subscription ? (
          <OnboardingChecklist data={onboarding} editable={false} />
        ) : null}
      </Card>

      <Card
        title="Solutions"
        subtitle="WhatsApp and email: what Balsam runs for you beyond the plan"
        className="card--table"
      >
        {solutions === null && !error ? <PageLoader label="Loading solutions…" /> : null}
        {solutions ? (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Solution</th>
                  <th>Provider</th>
                  <th>Plan</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {solutions
                  .filter((s) => s.slug !== 'platform')
                  .map((s) => (
                    <SolutionRow key={s.slug} solution={s} subscription={live(s.slug)} />
                  ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </Card>
    </>
  );
}

function SolutionRow({
  solution: s,
  subscription: sub,
}: {
  solution: Solution;
  subscription: SubscriptionRecord | null;
}) {
  const link =
    s.slug === 'whatsapp' ? '/portal/whatsapp' : s.slug === 'email' ? '/portal/email' : null;
  return (
    <tr>
      <td>{link ? <Link to={link}>{s.name}</Link> : s.name}</td>
      <td>{s.provider}</td>
      <td>{sub ? sub.plan.name : s.includedWithOnboarding ? 'Included' : '—'}</td>
      <td>
        {sub ? (
          <Badge tone={subscriptionTone(sub.status)}>enabled</Badge>
        ) : s.includedWithOnboarding ? (
          <Badge tone="accent">included</Badge>
        ) : (
          <Badge tone="neutral">not enabled</Badge>
        )}
      </td>
    </tr>
  );
}
